/* eslint-disable prettier/prettier */
import { FC, useEffect, useState } from 'react'
import { ColorOptions, RadiiOptions, SpaceOptions } from '../../theme/UiThemeProvider'
import { useBreakPointProps } from '../../utils/utils'
import { Box } from '../BoxNew'
import { shadowTypes } from '../BoxNew/ShadowTypes'
import { Icon, IconOptions } from '../IconNew'
import { Loader } from '../Loader'
import { SpacerNew } from '../SpacerNew'
import { Text, TextTypesOptions } from '../TextNew'
import { buttonTypes } from './ButtonTypes'

export type ButtonTypesOptions = keyof typeof buttonTypes
export const ButtonTypesOptionArray = Object.keys(buttonTypes)

export interface ButtonProps {
  type?: keyof typeof buttonTypes | Array<keyof typeof buttonTypes>
  onPress: (event?: any) => void | Promise<any> | Array<() => void | Promise<any>>
  isLoading?: boolean | Array<boolean>
  disabled?: boolean | Array<boolean>
  title?: string | Array<string>
  small?: boolean | Array<boolean>
  icon?: IconOptions
  testID?: string
  isRaceTrack?: boolean
  paddingVertical?: SpaceOptions | number | Array<SpaceOptions | number>
  borderColor?: ColorOptions | Array<ColorOptions>
  paddingHorizontal?: SpaceOptions | number | Array<SpaceOptions | number>
  titleColor?: ColorOptions | Array<ColorOptions>
  shadowType?: keyof typeof shadowTypes | Array<keyof typeof shadowTypes>
  borderRadius?: RadiiOptions | string | number | Array<RadiiOptions | string | number>
  borderWidth?: SpaceOptions | number | Array<SpaceOptions | number>
  textType?: TextTypesOptions | Array<TextTypesOptions>
  iconPaddingRight?: SpaceOptions | number
  width?: number | string | Array<number | string>
  height?: number | string | Array<number | string>
  hoverColor?: ColorOptions | Array<ColorOptions>
  underlayColor?: ColorOptions | Array<ColorOptions>
  color?: ColorOptions | Array<ColorOptions>
  hide?: boolean
  enableDot?: boolean
  textDecorationLine?: 'none' | 'underline' | 'line-through' | 'underline line-through'
  loadingColor?: ColorOptions
  opacity?: number,
  disabledColor?: ColorOptions
  disabledTitleColor?: ColorOptions
}

export const Button: FC<ButtonProps> = (props) => {
  const { children, testID = 'btn', hide } = props
  const modifiedProps = useBreakPointProps<ButtonProps>(props)
  const {
    type = 'primary',
    isLoading: propsLoading = false,
    onPress,
    title,
    disabled = false,
    icon,
    isRaceTrack = false,
    paddingVertical,
    color,
    borderColor,
    paddingHorizontal = 16,
    titleColor,
    shadowType,
    borderRadius,
    borderWidth,
    textType,
    iconPaddingRight = 'SMALL',
    width,
    height,
    hoverColor,
    underlayColor,
    enableDot,
    textDecorationLine,
    loadingColor,
    disabledColor,
    disabledTitleColor,
    opacity,
  } = modifiedProps
  const [pressState, setPressState] = useState(false)
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(propsLoading as boolean)
  }, [propsLoading])

  if (hide) {
    return null
  }

  const isAsync = (fn) => {
    return fn?.constructor?.name === 'AsyncFunction'
  }

  const onPressInternal = async (evt?: any) => {
    if (isLoading || disabled) {
      return
    }
    try {
      isAsync(onPress) && setLoading(true)
      if (onPress) {
        await onPress(evt)
      }
      isAsync(onPress) && setLoading(false)
    } catch (ex) {
      setLoading(false)
      throw ex
    }
  }

  const iconComponent = icon ? (
    <>
      <Icon
        id={icon}
        type={type.includes('primary') || type.includes('custom') ? 'primary' : 'secondary'}
        color={titleColor ? titleColor : buttonTypes[type as keyof typeof buttonTypes].text(pressState).color}
        enableDot={enableDot}
      />
      <SpacerNew direction={'horizontal'} size={iconPaddingRight} />
    </>
  ) : null

  const contents = children ? (
    children
  ) : (
    <Box flexDirection={'row'} alignItems={'center'}>
      {iconComponent}
      <Text
        testID={`${testID}-${title}`}
        {...buttonTypes[type as keyof typeof buttonTypes].text(pressState)}
        type={textType ? textType : buttonTypes[type as keyof typeof buttonTypes].text(pressState).type}
        color={
          disabled && disabledTitleColor
            ? disabledTitleColor as ColorOptions //('SECONDARY_GREY' as ColorOptions)
            : titleColor
              ? titleColor
              : buttonTypes[type as keyof typeof buttonTypes].text(pressState).color
        }
        textDecorationLine={textDecorationLine}
        selectable={false}>
        {title}
      </Text>
    </Box>
  )

  return (
    <Box {...buttonTypes[type as keyof typeof buttonTypes].buttonContainer} paddingVertical={paddingVertical}>
      <Box
        testID={`btn-wrapper-${testID}`}
        disabled={disabled}
        onPress={onPressInternal}
        onPressIn={() => setPressState(true)}
        onPressOut={() => setPressState(false)}
        paddingHorizontal={paddingHorizontal}
        opacity={disabled ? buttonTypes[type as keyof typeof buttonTypes].disabledOpacity : 1}
        {...buttonTypes[type as keyof typeof buttonTypes].container}
        borderColor={borderColor}
        shadowType={shadowType}
        width={width ?? buttonTypes[type as keyof typeof buttonTypes].container.width}
        height={height ?? buttonTypes[type as keyof typeof buttonTypes].container.height}
        backgroundColor={
          disabled && disabledColor
            ? disabledColor as ColorOptions //('DISABLED_GREY')
            : color ?? buttonTypes[type as keyof typeof buttonTypes].container.backgroundColor
        }
        hoverColor={hoverColor ?? buttonTypes[type as keyof typeof buttonTypes].container.hoverColor}
        underlayColor={underlayColor ?? buttonTypes[type as keyof typeof buttonTypes].container.underlayColor}
        borderRadius={
          borderRadius ? borderRadius : buttonTypes[type as keyof typeof buttonTypes].container.borderRadius
        }
        borderWidth={borderWidth ? borderWidth : buttonTypes[type as keyof typeof buttonTypes].container.borderWidth}
        {...(isRaceTrack && { borderRadius: 100 })}>
        {isLoading ? (
          <Loader
            color={
              loadingColor ??
              (type.toString().toLowerCase().indexOf('primary') > -1 ||
                type.toString().toLowerCase().indexOf('custom') > -1
                ? 'BRAND_PRIMARY_WHITE'
                : 'BRAND_PRIMARY_ACTION')
            }
          />
        ) : (
          contents
        )}
      </Box>
    </Box>
  )
}
